/* ============================================================
   MEMBER-SCOPED BROWSER STORAGE

   Storage belongs to the browser, not to the member. Anything a
   member leaves in it — the shop location they last used, their
   cart, a booking club — is read back by whoever logs in next on
   that browser. That is how a Tee Box UAT member opened the Shop at
   Tee Box Test Acct: a previous member's `teebox_selected_location`
   outranked their own home location.

   v1's sign-out wiped all of localStorage. v2's cleared only the
   auth keys, so everything else carried over.

   Two guards, because neither alone is enough:
     - `clearMemberSession()` on sign-out.
     - `startMemberSession(id)` on login clears when a DIFFERENT
       member logs in. That covers what sign-out cannot: a session
       that expired (which keeps the member's cart for their own
       return), and browsers already holding stale data from before
       this existed.

   Device preferences — UI version, theme, install prompt, the
   welcome name — are not member data and are left alone.

   The key names are owned by the files noted beside them; a new
   per-member key must be added here too.
   ============================================================ */

const MEMBER_LOCAL_KEYS = [
  'token',
  'user',
  'userLocation', // hooks/useUserLocation.ts
  'teebox_selected_location', // hooks/useShopLocation.ts, pages/Shop/Shop.tsx
  'teebox_shop_cart', // context/CartContext.tsx
  'activeSession', // utils/sessionTimer.ts
  'memberWasBlocked', // hooks/useMemberAccess.ts
  'tb.community.enabled', // v2/components/TabBar.tsx
];

const MEMBER_SESSION_KEYS = [
  'tb.booking-location', // v2/lib/bookingLocation.ts
  'membershipBannerDismissedStatus', // components/ui/organisms/MembershipStatusBanner.tsx
];

/** Fired after a clear, for state that was read from storage before the
 *  clear and lives on in memory — the cart is loaded at app start, and
 *  login moves on with `navigate()`, not a reload. */
export const MEMBER_SESSION_CLEARED_EVENT = 'tb:member-session-cleared';

/** Survives sign-out on purpose: it is how login tells a returning
 *  member from a different one. */
const LAST_MEMBER_KEY = 'tb.last-member-id';

function removeAll(storage: Storage, keys: string[]) {
  for (const key of keys) {
    try {
      storage.removeItem(key);
    } catch {
      /* Storage unavailable — nothing was persisted to leak. */
    }
  }
}

/** Remove everything that belongs to the member who is signed in. */
export function clearMemberSession(): void {
  removeAll(localStorage, MEMBER_LOCAL_KEYS);
  removeAll(sessionStorage, MEMBER_SESSION_KEYS);
  window.dispatchEvent(new Event(MEMBER_SESSION_CLEARED_EVENT));
}

/**
 * Call on login, BEFORE writing the new token. Clears the previous
 * member's data when someone else logs in; a member returning to their
 * own browser keeps their cart and shop choice.
 */
export function startMemberSession(memberId: string): void {
  let previous: string | null = null;
  try {
    previous = localStorage.getItem(LAST_MEMBER_KEY);
  } catch {
    /* Treat unreadable as unknown, which clears — the safe side. */
  }

  if (previous !== memberId) clearMemberSession();

  try {
    localStorage.setItem(LAST_MEMBER_KEY, memberId);
  } catch {
    /* Next login just clears again. */
  }
}
